import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/axios";

type User = {
  id: string;
  email: string;
  workspaceId: string;
  role: string;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string) => {
    try {
      const res = await api.post("/auth/login", {
      email,
      password,
    });

      if (res.data.token) {
        localStorage.setItem("bp_token", res.data.token);
    setUser(res.data.user);
      } else {
        throw new Error("No token received from server");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("Invalid email or password");
      }
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("bp_token");
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem("bp_token");

    if (!token) {
      setLoading(false);
      return;
    }

    api
      .get("/auth/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => setUser(res.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}